import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Home           from './pages/Home';
import ProtectedRoute from './components/ProtectedRoute';
import BotControl    from './components/BotControl';
import MonitorPanel  from './components/MonitorPanel';
import TradeHistory  from './components/TradeHistory';
import Login         from './components/Login';
import Settings      from './components/Settings';
import LogViewer     from './components/LogViewer';
import BacktestPanel from './components/BacktestPanel';
import HelpModal     from './components/HelpModal';
import TwoFactorModal from './components/TwoFactorModal';
import { refreshSession, SESSION_MARKER } from './utils/apiFetch';
import SupportModal  from './components/SupportModal';
import useTrackPageView from './hooks/useTrackPageView';
import { useTranslation } from './i18n/LanguageContext';
import { useTheme } from './hooks/useTheme';
import { UserSettingsProvider } from './hooks/useUserSettings';
import './App.css';

const API_URL = '/api';

// Oturum, el sıkışmada tarayıcının gönderdiği HttpOnly çerezle doğrulanır (madde 17) — adreste token yok.
function wsUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/ws`;
}

export default function App() {
  const { t, lang, setLang } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Oturum HttpOnly çerezde (madde 17) — 'token' yalnızca "oturum var" işaretidir (SESSION_MARKER).
  const [token,          setToken]          = useState(() => {
    localStorage.removeItem('ts_token');   // eski sürümden kalan düz metin token
    return localStorage.getItem('ts_role') ? SESSION_MARKER : null;
  });
  const [role,           setRole]           = useState(() => localStorage.getItem('ts_role'));
  const [userType,       setUserType]       = useState(() => localStorage.getItem('ts_type') || 'system');
  // Platform operatörü = sistem ADMIN (viewer da user_type 'system' ama yönetici ekranlarını görmez).
  const isSystemAdmin = userType === 'system' && role === 'admin';
  const [subscription,   setSubscription]   = useState(() => localStorage.getItem('ts_sub') || 'active');
  const [userName,       setUserName]       = useState(() => localStorage.getItem('ts_name') || '');
  const [botState,       setBotState]       = useState({ running: false, sessions: [] });
  const [wsStatus,       setWsStatus]       = useState('connecting');
  const [analyses,       setAnalyses]       = useState({}); // symbol → analysis data
  const [events,         setEvents]         = useState([]);
  const [trades,         setTrades]         = useState([]);
  const [watchedTrades,  setWatchedTrades]  = useState([]); // izlenen açık işlemler
  const [showSettings,     setShowSettings]     = useState(false);
  const [showLogs,         setShowLogs]         = useState(false);
  const [showBacktest,     setShowBacktest]     = useState(false);
  const [showHelp,      setShowHelp]      = useState(false);
  const [showTfa,       setShowTfa]       = useState(false);   // İki adımlı doğrulama (madde 12)
  const [showSupport,       setShowSupport]       = useState(false);
  const [openTicketCount,   setOpenTicketCount]   = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // sağ üst ikon çubuğu, dar ekranda hamburger menü
  const [activeSymbol,   setActiveSymbol]   = useState(null);
  const [settingsSavedAt, setSettingsSavedAt] = useState(0);
  const [exchangeStatus, setExchangeStatus] = useState(null); // { baseUrl, status: 'valid'|'invalid'|'missing' } | null
  const [tracedSymbols,  setTracedSymbols]  = useState([]); // [{symbol, margin, leverage}]

  const wsRef          = useRef(null);
  const reconnectTimer = useRef(null);
  const loggingOut     = useRef(false);

  const handleLogin = (t, r, ut = 'system', sub = 'active', name = '') => {
    localStorage.setItem('ts_role', r);
    localStorage.setItem('ts_type', ut);
    localStorage.setItem('ts_sub',  sub);
    localStorage.setItem('ts_name', name);
    setRole(r);
    setUserType(ut);
    setSubscription(sub);
    setUserName(name);
    setToken(t);
  };

  const handleLogout = async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* ignore */ }
    localStorage.removeItem('ts_token');
    localStorage.removeItem('ts_role');
    localStorage.removeItem('ts_type');
    localStorage.removeItem('ts_sub');
    localStorage.removeItem('ts_name');
    setToken(null);
    setRole(null);
    setUserType('system');
    setSubscription('active');
    setUserName('');
    loggingOut.current = false;
    wsRef.current?.close();
    navigate('/', { replace: true });
  };

  // Google OAuth geri dönüşü — backend oturum çerezini koyup "/?ts_login=1" (veya hata durumunda
  // "/?auth_error=...") şeklinde köke yönlendirir; rota ne olursa olsun burada, uygulamanın
  // en üstünde, tek seferlik yakalanır (Login.jsx artık sadece /login'de mount olduğu için
  // OAuth callback'i orada YAKALAYAMAZ).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ts_login')) {
      // Google girişi: oturum çerezi backend'de konuldu; rol/ad bilgisini al.
      fetch(`${API_URL}/auth/me`)
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((d) => { handleLogin(SESSION_MARKER, d.role, d.user_type, d.subscription, d.name || ''); navigate('/panel', { replace: true }); })
        .catch(() => navigate('/login', { replace: true, state: { authError: 'google' } }));
    } else if (token) {
      // Açılışta oturum hâlâ geçerli mi (gerekirse wrapper yeniler); değilse çıkış.
      fetch(`${API_URL}/auth/me`)
        .then((r) => {
          if (r.status === 401) { handleLogout(); return null; }
          return r.ok ? r.json() : null;
        })
        .then((d) => d && handleLogin(SESSION_MARKER, d.role, d.user_type, d.subscription, d.name || ''))
        .catch(() => {});
    }
    if (params.get('auth_error')) {
      navigate('/login', { replace: true, state: { authError: params.get('auth_error') } });
    }
  }, []); // eslint-disable-line

  // ── Yardımcılar ──────────────────────────────────────────

  const addEvent = useCallback((ev) => {
    setEvents(prev => [{ ...ev, id: Date.now() + Math.random() }, ...prev].slice(0, 60));
  }, []);

  const authHeader = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchTrades = useCallback(async () => {
    if (!token) return;
    try {
      const res  = await fetch(`${API_URL}/trades?limit=500`, { headers: authHeader() });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      setTrades(Array.isArray(data) ? data : []);
    } catch { /* sunucu henüz hazır değil */ }
  }, [token]); // eslint-disable-line

  const fetchState = useCallback(async () => {
    if (!token) return;
    try {
      const res  = await fetch(`${API_URL}/bot/state`, { headers: authHeader() });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      setBotState(data);
    } catch { /* ignore */ }
  }, [token]); // eslint-disable-line

  // Bot Ayarları panelindeki "Aktif Borsa" satırı için — API kimlik bilgisi var mı VE gerçekten
  // OKX'e karşı geçerli mi (hafif bir bakiye sorgusu ile) test edilir.
  const fetchExchangeStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res  = await fetch(`${API_URL}/settings/exchange-status`, { headers: authHeader() });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      setExchangeStatus(data);
    } catch { /* ignore */ }
  }, [token]); // eslint-disable-line

  // Sadece sistem admin için — header'daki Destek rozetinde gösterilecek açık talep sayısı.
  const fetchOpenTicketCount = useCallback(async () => {
    if (!token || !isSystemAdmin) return;
    try {
      const res  = await fetch(`${API_URL}/support/admin/open-count`, { headers: authHeader() });
      if (!res.ok) return;
      const data = await res.json();
      setOpenTicketCount(data.count || 0);
    } catch { /* ignore */ }
  }, [token, isSystemAdmin]); // eslint-disable-line

  // ── WebSocket ─────────────────────────────────────────────

  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    if (!token) return;
    const ws = new WebSocket(wsUrl());
    wsRef.current = ws;

    ws.onopen = () => setWsStatus('connected');

    ws.onmessage = ({ data: raw }) => {
      let msg;
      try { msg = JSON.parse(raw); } catch { return; }

      switch (msg.type) {
        case 'connected':
          setBotState(msg.data || { running: false, sessions: [] });
          break;
        case 'bot_started': {
          const startedSym = msg.data?.symbol;
          fetchState();
          // tracedSymbols'ü koruyoruz — MonitorPanel bot sekmesi açıkken trace sekmesini
          // zaten filtreler; bot durduğunda trace sekmesi otomatik geri gelir
          addEvent({ label: t('app.eventBotStarted'), detail: startedSym, kind: 'ok' });
          break;
        }
        case 'bot_stopped':
          fetchState();
          setAnalyses(prev => { const n = { ...prev }; delete n[msg.data?.symbol]; return n; });
          addEvent({ label: t('app.eventBotStopped'), detail: msg.data?.symbol, kind: 'warn' });
          break;
        case 'ticker': {
          const sym = msg.data?.symbol;
          if (sym) setAnalyses(prev => prev[sym]
            ? { ...prev, [sym]: { ...prev[sym], currentPrice: msg.data.price } }
            : prev
          );
          break;
        }
        case 'analysis':
          setAnalyses(prev => ({ ...prev, [msg.data.symbol]: msg.data }));
          break;
        case 'trade_opened':
          addEvent({ label: t('app.eventTradeOpened'), detail: `${msg.data?.trade?.side?.toUpperCase()} ${msg.data?.trade?.symbol}`, kind: 'ok' });
          fetchTrades();
          break;
        case 'trade_closed': {
          const pnl  = parseFloat(msg.data?.pnl ?? 0);
          const sign = pnl >= 0 ? '+' : '';
          addEvent({
            label:  pnl >= 0 ? t('app.eventTradeClosedProfit') : t('app.eventTradeClosedLoss'),
            detail: `${msg.data?.symbol} ${sign}${pnl.toFixed(2)} USDT (fee: ${parseFloat(msg.data?.fee ?? 0).toFixed(4)})`,
            kind:   pnl >= 0 ? 'ok' : 'error',
          });
          // İzlenen işlem kapandıysa izlemeyi durdur
          if (msg.data?.symbol) {
            setWatchedTrades(prev => {
              if (prev.some(t => t.symbol === msg.data.symbol)) {
                fetch(`${API_URL}/monitor/stop`, {
                  method: 'POST', headers: authHeader(), body: JSON.stringify({ symbol: msg.data.symbol }),
                }).catch(() => {});
                return prev.filter(t => t.symbol !== msg.data.symbol);
              }
              return prev;
            });
          }
          fetchTrades();
          break;
        }
        case 'trade_updated':
          setTrades(prev => prev.map(t =>
            t.id === msg.data.id ? { ...t, ...msg.data } : t
          ));
          break;
        case 'sl_cooldown':
          addEvent({
            label:  t('app.eventSlCooldown'),
            detail: t('app.eventSlCooldownDetail', { symbol: msg.data?.symbol, min: msg.data?.durationMin }),
            kind:   'warn',
          });
          break;
        case 'signal_skipped':
          addEvent({ label: t('app.eventSignalSkipped'), detail: msg.data?.reason, kind: 'warn' });
          break;
        case 'error':
          addEvent({ label: t('app.eventError'), detail: msg.data?.message, kind: 'error' });
          break;
        case 'pong':
          break;
        default:
          break;
      }
    };

    ws.onclose = (ev) => {
      setWsStatus('disconnected');
      if (ev.code === 4401) {
        // Erişim çerezinin süresi dolmuş (madde 17): yenile ve hemen yeniden bağlan; yenilenemezse çıkış.
        refreshSession().then((ok) => {
          if (ok) reconnectTimer.current = setTimeout(connectWs, 300);
          else handleLogout();
        });
        return;
      }
      reconnectTimer.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => setWsStatus('error');
  }, [addEvent, fetchTrades, fetchState]);

  useEffect(() => {
    connectWs();
    fetchTrades();
    fetchState();
    fetchExchangeStatus();
    fetchOpenTicketCount();
    return () => {
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connectWs, fetchTrades, fetchState, fetchExchangeStatus, fetchOpenTicketCount]);

  // API anahtarları Ayarlar'dan değiştirilip kaydedildiğinde durumu tazele.
  useEffect(() => {
    if (settingsSavedAt) fetchExchangeStatus();
  }, [settingsSavedAt]); // eslint-disable-line

  // ── API Çağrıları ──────────────────────────────────────────

  const handleTrace = useCallback(async (cfg) => {
    const sym = cfg.symbol;
    const alreadyTraced = tracedSymbols.find(t => t.symbol === sym);
    if (alreadyTraced) {
      // Ayarlar değişmişse güncelle (kapamak için MonitorPanel'deki × butonunu kullan)
      setTracedSymbols(prev => prev.map(t => t.symbol === sym ? cfg : t));
    } else {
      // Yeni takip ekle
      const alreadyMonitored = watchedTrades.find(t => t.symbol === sym)
        || (botState?.sessions || []).find(s => s.symbol === sym);
      if (!alreadyMonitored) {
        fetch(`${API_URL}/monitor/start`, {
          method: 'POST', headers: authHeader(), body: JSON.stringify({ symbol: sym, market: cfg.market }),
        }).catch(() => {});
      }
      setTracedSymbols(prev => [...prev, cfg]);
    }
  }, [tracedSymbols, watchedTrades, botState, authHeader]);

  const handleUnTrace = useCallback(async (symbol) => {
    const stillWatched = watchedTrades.find(t => t.symbol === symbol);
    const stillRunning = (botState?.sessions || []).find(s => s.symbol === symbol);
    if (!stillWatched && !stillRunning) {
      fetch(`${API_URL}/monitor/stop`, {
        method: 'POST', headers: authHeader(), body: JSON.stringify({ symbol }),
      }).catch(() => {});
    }
    setTracedSymbols(prev => prev.filter(t => t.symbol !== symbol));
  }, [watchedTrades, botState, authHeader]);

  const handleStart = async (cfg) => {
    const res  = await fetch(`${API_URL}/bot/start`, {
      method: 'POST', headers: authHeader(), body: JSON.stringify(cfg),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || t('app.errStartFailed'));
    return data;
  };

  const handleWatch = async (trade) => {
    if (watchedTrades.find(t => t.id === trade.id)) return; // zaten izleniyor
    await fetch(`${API_URL}/monitor/start`, {
      method: 'POST', headers: authHeader(), body: JSON.stringify({ symbol: trade.symbol }),
    });
    setWatchedTrades(prev => [...prev, trade]);
  };

  const handleUnwatch = async (symbol) => {
    await fetch(`${API_URL}/monitor/stop`, {
      method: 'POST', headers: authHeader(), body: JSON.stringify({ symbol }),
    });
    setWatchedTrades(prev => prev.filter(t => t.symbol !== symbol));
    setAnalyses(prev => { const n = { ...prev }; delete n[symbol]; return n; });
  };

  const handleStop = async (symbol) => {
    const res  = await fetch(`${API_URL}/bot/stop`, {
      method: 'POST', headers: authHeader(), body: JSON.stringify({ symbol }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || t('app.errStopFailed'));
    return data;
  };

  const handleCloseTrade = async (id) => {
    const res  = await fetch(`${API_URL}/trades/${id}/close`, { method: 'POST', headers: authHeader() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || t('app.errCloseFailed'));
    fetchTrades();
  };

  const openTrades = trades.filter(t => t.status === 'open');
  const sessions   = botState?.sessions || [];

  // Pencere/sekme geçişleri (GA4) — hook'lar koşulsuz çağrılmalı (React kuralları), bu yüzden
  // component'in en üst seviyesinde dururlar; `active` bayrakları /panel rotasında olup
  // olmadığımızı da kapsar (Home/Login'deyken "Ana Ekran" pageview'ı tetiklenmesin diye). Ayarlar/
  // Backtest/Yardım burada AYRICA izlenmez — kendi sekmeleriyle birlikte daha spesifik bir
  // page_view'ı zaten mount olur olmaz gönderiyorlar (çift kayıt olmasın diye).
  const onPanel = location.pathname === '/panel';
  const noModalOpen = !showSettings && !showLogs && !showBacktest && !showHelp && !showTfa;
  useTrackPageView('Backend Loglari',  !!token && onPanel && showLogs);
  useTrackPageView('Ana Ekran',        !!token && onPanel && noModalOpen);

  const dashboard = (
    <UserSettingsProvider token={token}>
    <div className="app">
      {showSettings && role === 'admin' && (
        <Settings
          token={token}
          userType={userType}
          onClose={() => setShowSettings(false)}
          onSave={() => setSettingsSavedAt(Date.now())}
        />
      )}
      {showLogs && (
        <LogViewer token={token} onClose={() => setShowLogs(false)} />
      )}
      {showBacktest && role === 'admin' && (
        <BacktestPanel token={token} onClose={() => setShowBacktest(false)} />
      )}
      {showTfa && <TwoFactorModal token={token} onClose={() => setShowTfa(false)} />}
      {showHelp && (
        <HelpModal role={role} onClose={() => setShowHelp(false)} />
      )}
      {showSupport && (
        <SupportModal token={token} isSystemAdmin={isSystemAdmin} onClose={() => { setShowSupport(false); fetchOpenTicketCount(); }} />
      )}

      {/* ── Header ── */}
      <header className="app-header">
        <div className="header-left">
          <div className="logo">
            <img src="/logo.png" alt="TrendSniper" className="logo-img" />
            <span className="logo-text">TrendSniper</span>
          </div>
        </div>
        <button
          className="header-hamburger-btn"
          title={t('app.menu')}
          onClick={() => setMobileMenuOpen(o => !o)}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        <div className={`header-right${mobileMenuOpen ? ' open' : ''}`} onClick={() => setMobileMenuOpen(false)}>
          <div
            className={`ws-badge ws-${wsStatus}`}
            title={
              wsStatus === 'connected'   ? t('app.wsConnected')
              : wsStatus === 'connecting' ? t('app.wsConnecting')
              : wsStatus === 'error'      ? t('app.wsError')
              :                             t('app.wsReconnecting')
            }
          >
            <span className="ws-dot" />
            <span className="header-right-label">
              {wsStatus === 'connected'   ? t('app.wsConnected')
              : wsStatus === 'connecting' ? t('app.wsConnecting')
              : wsStatus === 'error'      ? t('app.wsError')
              :                             t('app.wsReconnecting')}
            </span>
          </div>
          <button
            className="settings-btn"
            title={t('app.langButtonTitle')}
            onClick={() => setLang(lang === 'tr' ? 'en' : 'tr')}
            style={{ fontWeight: 700, fontSize: 12 }}
          >
            <span>{lang === 'tr' ? 'EN' : 'TR'}</span><span className="header-right-label">{t('app.langButtonTitle')}</span>
          </button>
          <button
            className="settings-btn"
            title={t('app.themeButtonTitle')}
            onClick={toggleTheme}
          >
            <span>{theme === 'light' ? '🌙' : '☀️'}</span><span className="header-right-label">{t('app.themeButtonTitle')}</span>
          </button>
          {isSystemAdmin && (
            <button className="log-btn" title={t('app.logs')} onClick={() => setShowLogs(true)}><span>▤</span><span className="header-right-label">{t('app.logs')}</span></button>
          )}
          {role === 'admin' && (
            <button className="settings-btn" title={t('app.backtest')} onClick={() => setShowBacktest(true)}><span>📊</span><span className="header-right-label">{t('app.backtest')}</span></button>
          )}
          <span className="support-btn-wrap">
            <button className="settings-btn" title={t('app.support')} onClick={() => setShowSupport(true)}><span>🎧</span><span className="header-right-label">{t('app.support')}</span></button>
            {isSystemAdmin && openTicketCount > 0 && (
              <span className="support-count-badge">{openTicketCount}</span>
            )}
          </span>
          <button className="settings-btn" title={t('app.twoFactor')} onClick={() => setShowTfa(true)}><span>🔐</span><span className="header-right-label">{t('app.twoFactor')}</span></button>
          <button className="settings-btn" title={t('app.help')} onClick={() => setShowHelp(true)}><span>❔</span><span className="header-right-label">{t('app.help')}</span></button>
          {role === 'admin' && (
            <button className="settings-btn" title={t('app.settings')} onClick={() => setShowSettings(true)}><span>⚙</span><span className="header-right-label">{t('app.settings')}</span></button>
          )}
          <button className="logout-btn" title={t('app.logout')} onClick={handleLogout}><span>→</span><span className="header-right-label">{t('app.logout')}</span></button>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="app-main">
        <div className={`grid-layout${role === 'viewer' ? ' grid-viewer' : ''}`}>

          {/* Sol panel — bot kontrolü (admin only) */}
          {role !== 'viewer' && (
            <section className="card control-card">
              <BotControl
                botState={botState}
                onStop={handleStop}
                activeSymbol={activeSymbol}
                subscription={subscription}
                userType={userType}
                onTrace={handleTrace}
                tracedSymbols={tracedSymbols}
                userName={userName}
                openTrades={openTrades}
                onCloseTrade={handleCloseTrade}
                exchangeStatus={exchangeStatus}
              />
            </section>
          )}

          {/* Sağ üst — Canlı Durum + Grafik (sekmeli) */}
          <section className="card monitor-card">
            <MonitorPanel
              sessions={sessions}
              watchedTrades={watchedTrades}
              analyses={analyses}
              openTrades={openTrades}
              events={events}
              token={token}
              subscription={subscription}
              userType={userType}
              onUnwatch={handleUnwatch}
              onStop={handleStop}
              onActiveSymbolChange={setActiveSymbol}
              settingsSavedAt={settingsSavedAt}
              tracedSymbols={tracedSymbols}
              onUnTrace={handleUnTrace}
              onStartBot={handleStart}
              onCloseTrade={handleCloseTrade}
              onManualTrade={async (cfg) => {
                const res  = await fetch(`${API_URL}/trades/manual`, {
                  method: 'POST', headers: authHeader(), body: JSON.stringify(cfg),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || t('app.errOpenFailed'));
                fetchTrades();
                return data;
              }}
              onAdjustTpSl={async (tradeId, payload) => {
                const res  = await fetch(`${API_URL}/trades/${tradeId}/adjust-tpsl`, {
                  method: 'POST', headers: authHeader(), body: JSON.stringify(payload),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || t('app.errTpSlFailed'));
                fetchTrades();
                return data;
              }}
            />
          </section>

          {/* Sağ alt — İşlem Geçmişi */}
          <section className="card history-card">
            <TradeHistory
              trades={trades}
              onRefresh={fetchTrades}
              isAdmin={role === 'admin'}
              token={token}
              watchedIds={watchedTrades.map(t => t.id)}
              onWatch={handleWatch}
              onClose={handleCloseTrade}
              onDelete={async (ids) => {
                await fetch(`${API_URL}/trades`, {
                  method: 'DELETE',
                  headers: authHeader(),
                  body: JSON.stringify(ids ? { ids } : {}),
                });
                fetchTrades();
              }}
            />
          </section>

        </div>
      </main>

      <footer className="app-footer">
        <a href="https://uygarteknoloji.com.tr" target="_blank" rel="noopener noreferrer">
          Uygar Teknoloji
        </a>
      </footer>
    </div>
    </UserSettingsProvider>
  );

  return (
    <Routes>
      <Route path="/" element={<Home isAuthenticated={!!token} />} />
      <Route path="/login" element={token ? <Navigate to="/panel" replace /> : <Login onLogin={handleLogin} />} />
      <Route element={<ProtectedRoute isAuthenticated={!!token} />}>
        <Route path="/panel" element={dashboard} />
      </Route>
      <Route path="*" element={<Navigate to={token ? '/panel' : '/'} replace />} />
    </Routes>
  );
}
